import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface CaptureCardButton {
  text: string
  type: 'url' | 'postback'
  value: string
}

export interface CaptureCard {
  id: string
  name: string
  title: string
  subtitle: string | null
  imageUrl: string
  imageHasFilter: boolean
  buttonText: string
  buttonUrl: string | null
  buttonType: 'url' | 'postback'
  buttonPayload: string | null
  secondaryButtonText: string | null
  secondaryButtonUrl: string | null
  secondaryButtonType: 'url' | 'postback' | null
  secondaryButtonPayload: string | null
  buttons: CaptureCardButton[]
  publicReplyText: string | null
  isActive: boolean
  sentCount: number
  clickCount: number
  todayClicks: number
  errorCount: number
  lastSentAt: string | null
  lastClickAt: string | null
  accountIds: string[]
  createdAt: string | null
  updatedAt: string | null
}

export interface CaptureCardStatsPoint {
  date: string
  label: string
  clicks: number
}

export interface CaptureCardSendTotals {
  total: number
  ok: number
  fail: number
}

export interface CaptureCardFailureRow {
  page: string
  kind: 'pagina' | 'grupo'
  reason: string
  code: string | null
  count: number
  lastAt: string
}

export interface CaptureCardStats {
  todayClicks: number
  totalClicks: number
  cardsWithClicksToday: number
  chart: CaptureCardStatsPoint[]
  sendsToday?: CaptureCardSendTotals & {
    pagina: CaptureCardSendTotals
    grupo: CaptureCardSendTotals
    reasons: Record<string, number>
  }
  sendsByCardToday?: Record<string, CaptureCardSendTotals>
  failuresToday?: CaptureCardFailureRow[]
  topCardsToday: Array<{
    id: string
    name: string
    todayClicks: number
    totalClicks: number
  }>
}

export interface CaptureCardAccount {
  id: string
  name: string
  pageId: string
  igUserId: string
  isActive: boolean
  fbActive: boolean
  /** Instagram: 'broadzin' quando a conta já tem o token de mensagens do Broadzinho */
  msgApp?: 'broadzin' | null
}

/** 'fb' = tela Card de Captura (Facebook, padrão); 'ig' = Card Captura do Instagram */
export type CaptureCardPlatform = 'fb' | 'ig'

// No Facebook as URLs e o corpo ficam iguais aos de sempre; só o Instagram manda platform.
const qs = (platform: CaptureCardPlatform) => (platform === 'ig' ? '?platform=ig' : '')
const withPlatform = <T extends object>(platform: CaptureCardPlatform, body: T) =>
  platform === 'ig' ? { ...body, platform: 'ig' } : body
const listKey = (platform: CaptureCardPlatform, ...rest: string[]) =>
  platform === 'ig' ? ['capture-cards', 'ig', ...rest] : ['capture-cards', ...rest]

export interface CaptureCardPayload {
  name: string
  title: string
  subtitle?: string | null
  imageUrl: string
  imageHasFilter?: boolean
  buttonText?: string
  buttonUrl?: string | null
  buttons: CaptureCardButton[]
  publicReplyText?: string | null
  isActive?: boolean
  accountIds?: string[]
}

export function useCaptureCards(refetchInterval: number | false = false, platform: CaptureCardPlatform = 'fb') {
  return useQuery<CaptureCard[]>({
    queryKey: listKey(platform),
    queryFn: () => api.get<CaptureCard[]>('/capture-cards' + qs(platform)),
    refetchInterval,
  })
}

export function useCaptureCardStats(refetchInterval: number | false = false, platform: CaptureCardPlatform = 'fb') {
  return useQuery<CaptureCardStats>({
    queryKey: listKey(platform, 'stats'),
    queryFn: () => api.get<CaptureCardStats>('/capture-cards/stats' + qs(platform)),
    refetchInterval,
  })
}

export function useCaptureCardAccounts(platform: CaptureCardPlatform = 'fb') {
  return useQuery<CaptureCardAccount[]>({
    queryKey: listKey(platform, 'accounts'),
    queryFn: () => api.get<CaptureCardAccount[]>('/capture-cards/available-accounts' + qs(platform)),
  })
}

export function useCreateCaptureCard(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CaptureCardPayload) =>
      api.post<CaptureCard>('/capture-cards', withPlatform(platform, payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useUpdateCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CaptureCardPayload }) =>
      api.put<CaptureCard>(`/capture-cards/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useDeleteCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/capture-cards/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useToggleCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: boolean; isActive: boolean }>(`/capture-cards/${id}/toggle`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useBulkUpdateUrl(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { newUrl: string; buttonIndex?: number }) =>
      api.post<{ ok: boolean; updated: number }>('/capture-cards/bulk-update-url', withPlatform(platform, data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useImportPresets(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (presetIds: string[]) =>
      api.post<{ ok: boolean; created: number; ids: string[] }>('/capture-cards/import-presets', withPlatform(platform, { presetIds })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkActivate(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; activated: number }>('/capture-cards/bulk-activate', withPlatform(platform, {})),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkDeactivate(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; deactivated: number }>('/capture-cards/bulk-deactivate', withPlatform(platform, {})),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkAssignAll(platform: CaptureCardPlatform = 'fb') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; assigned: number; cards: number; accounts: number }>('/capture-cards/bulk-assign-all', withPlatform(platform, {})),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}
